import {CommentsPreference, DiffLineSpacingPreference} from '@github-ui/diff-view-settings/types'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'

export const COMPACT_LINE_HEIGHT = 20
export const RELAXED_LINE_HEIGHT = 25
export const DIFF_HEADER_SIZE = 50
export const ESTIMATED_COMMENT_HEIGHT = 500
export const ESTIMATED_ANNOTATION_HEIGHT = 300
export const ESTIMATED_CONTEXT_LINES = 6
export const ESTIMATED_LINES_PER_HUNK = 5

/**
 * Estimates the rendered height (in pixels) of a diff view for a file in a pull request.
 *
 * This function calculates an approximate height based on several factors:
 * - Number of changed lines in the diff
 * - User's line spacing preference (compact vs relaxed)
 * - Number and type of comments/annotations (if visible)
 * - Whether the file has been marked as viewed (collapsed state)
 *
 * The estimation is used for virtualization and performance optimization when rendering
 * large pull request diffs.
 *
 * @param params - Configuration object for the estimation
 * @param params.diffSummary - Summary data for the diff, including lines changed, markers, and view state
 * @param params.commentPreference - User preference for comment visibility (visible/hidden)
 * @param params.lineSpacingPreference - User preference for line spacing (compact/relaxed)
 * @param params.isCollapsed - Whether the diff is collapsed
 * @returns Estimated height in pixels for the rendered diff view
 *
 * @example
 * ```ts
 * const height = estimateDiffSize({
 *   diffSummary: {
 *     linesChanged: 150,
 *     markedAsViewed: false,
 *     markersMap: { ... }
 *   },
 *   commentPreference: CommentsPreference.Visible,
 *   lineSpacingPreference: DiffLineSpacingPreference.Compact,
 *   isCollapsed: false
 * })
 * // Returns: ~3550 (50 header + 3000 lines + comments/annotations)
 * ```
 */
export function estimateDiffSize({
  diffSummary,
  commentPreference,
  lineSpacingPreference,
  isCollapsed,
}: {
  diffSummary: PullRequestFileTreeDiff | undefined
  commentPreference: CommentsPreference
  lineSpacingPreference: DiffLineSpacingPreference
  isCollapsed: boolean
}) {
  const heightPerLine =
    lineSpacingPreference === DiffLineSpacingPreference.Compact ? COMPACT_LINE_HEIGHT : RELAXED_LINE_HEIGHT

  const estimatedHunks = diffSummary ? Math.max(1, Math.ceil(diffSummary.linesChanged / ESTIMATED_LINES_PER_HUNK)) : 1
  // Add an arbitrary buffer for context lines because overestimating is better than underestimating for virtualization
  const estimatedContextLinesHeight = estimatedHunks * ESTIMATED_CONTEXT_LINES * heightPerLine

  let estimatedCommentHeight = 0
  let estimatedAnnotationHeight = 0

  if (isCollapsed) {
    return DIFF_HEADER_SIZE
  }

  // Estimate comments height
  if (commentPreference === CommentsPreference.Visible) {
    // Iterate over the values of the diffSummary?.markersMap and keep a count of threads and annotations
    let threadCount = 0
    let annotationCount = 0

    const markers = Object.values(diffSummary?.markersMap || {})
    for (const markerPosition of markers) {
      threadCount += markerPosition.threads.length
      annotationCount += markerPosition.annotations.length
    }
    estimatedCommentHeight = threadCount * ESTIMATED_COMMENT_HEIGHT
    estimatedAnnotationHeight = annotationCount * ESTIMATED_ANNOTATION_HEIGHT
  }

  // Diff summaries do not have knowledge of generated files or too large diffs. We skip this for now.
  // Context lines depend on the number of hunks plus any additional context added for comments outside the diff.
  // We provide an arbitrary estimate for context lines.

  const linesChanged = diffSummary?.linesChanged ?? 5
  const lineHeight = heightPerLine * linesChanged
  return (
    DIFF_HEADER_SIZE + lineHeight + estimatedCommentHeight + estimatedAnnotationHeight + estimatedContextLinesHeight
  )
}
